import { ReactNode } from 'react';

import { CanvasElement, ConnectorType, ElementStyle, LineStyle } from '@/features/canvas/types';
import { cn } from '@/components/ui/cn';
import { GLASS } from './glass';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { MS, MS_ICON } from './ms';
import { ToolbarAnchor } from './inspector';

const seg = 'flex h-8 w-8 items-center justify-center rounded-lg transition-colors';
const active = 'bg-brand text-white';
const idle = 'text-fg-muted hover:bg-layer hover:text-fg';

const Ico = ({ children }: { children: ReactNode }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const TYPE_ICON: Record<ConnectorType, ReactNode> = {
  straight: <Ico><path d="M4 20L20 4" /></Ico>,
  elbow: <Ico><path d="M4 20h6v-8h10" /></Ico>,
  curved: <Ico><path d="M4 20c6 0 4-14 16-14" /></Ico>,
};
const STYLE_DASH: Record<LineStyle, string | undefined> = { solid: undefined, dashed: '5 4', dotted: '2 4' };

interface Props {
  connector: CanvasElement;
  anchor: ToolbarAnchor | null;
  onStyle: (patch: Partial<ElementStyle>) => void;
  onColor: (c: string) => void;
  onReverse: () => void;
  onAddText: () => void;
  onDelete: () => void;
}

/** The floating context toolbar for a selected connector: routing type, stroke style, arrowheads,
 *  colour, reverse direction, add text, delete — floats above the connector like the object toolbar. */
export default function ConnectorInspector({ connector, anchor, onStyle, onColor, onReverse, onAddText, onDelete }: Props) {
  const s = connector.style ?? {};
  const type = s.connectorType ?? 'curved';
  const line = s.lineStyle ?? 'solid';
  const arrowEnd = s.arrowEnd !== false;
  const arrowStart = !!s.arrowStart;
  const color = connector.color ?? '#868e96';

  if (!anchor) return null;
  const above = anchor.top > 92;
  const top = above ? anchor.top - 12 : anchor.bottom + 12;
  const translate = above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';

  return (
    <div
      data-wb-overlay
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(GLASS, 'pointer-events-auto fixed z-30 flex items-center gap-1 rounded-2xl px-2 py-1.5')}
      style={{ left: anchor.x, top, transform: translate }}
    >
      {/* reverse direction */}
      <button className={cn(seg, idle)} title="Reverse direction" onClick={onReverse}>
        <MS icon={MS_ICON.swap_horiz} size={19} />
      </button>
      <span className="mx-0.5 h-5 w-px bg-line" />

      {/* line type */}
      {(['straight', 'elbow', 'curved'] as ConnectorType[]).map((t) => (
        <button key={t} className={cn(seg, type === t ? active : idle)} title={`${t} line`} onClick={() => onStyle({ connectorType: t })}>
          {TYPE_ICON[t]}
        </button>
      ))}
      <span className="mx-0.5 h-5 w-px bg-line" />

      {/* line style */}
      {(['solid', 'dashed', 'dotted'] as LineStyle[]).map((ls) => (
        <button key={ls} className={cn(seg, line === ls ? active : idle)} title={`${ls} stroke`} onClick={() => onStyle({ lineStyle: ls })}>
          <svg width="20" height="8" viewBox="0 0 20 8">
            <line x1="1" y1="4" x2="19" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray={STYLE_DASH[ls]} />
          </svg>
        </button>
      ))}
      <span className="mx-0.5 h-5 w-px bg-line" />

      {/* arrowheads */}
      <button className={cn(seg, arrowStart ? active : idle)} title="Arrow at start" onClick={() => onStyle({ arrowStart: !arrowStart })}>
        <MS icon={MS_ICON.line_start_arrow} size={19} />
      </button>
      <button className={cn(seg, arrowEnd ? active : idle)} title="Arrow at end" onClick={() => onStyle({ arrowEnd: !arrowEnd })}>
        <MS icon={MS_ICON.line_end_arrow} size={19} />
      </button>
      <span className="mx-0.5 h-5 w-px bg-line" />

      <ColorPicker
        value={color}
        onChange={onColor}
        align="left"
        trigger={({ toggle }) => (
          <button type="button" title="Colour" onClick={toggle} className={cn(seg, idle)}>
            <span className="h-5 w-5 rounded-full ring-1 ring-inset ring-black/25" style={{ background: color }} />
          </button>
        )}
      />
      <button className={cn(seg, idle)} title="Add label" onClick={onAddText}>
        <MS icon={MS_ICON.text_fields} size={19} />
      </button>
      <button className="flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-rose-500/15 hover:text-rose-500" title="Delete (Del)" onClick={onDelete}>
        <MS icon={MS_ICON.delete} size={19} />
      </button>
    </div>
  );
}
